import React, { useEffect, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useStoreState } from "easy-peasy";

const eligibilityPeriodMs =
  (Number(process.env.REACT_APP_EXIT_OVERLAY_ELIGIBILITY_PERIOD_SECONDS) || 60) * 1000;

function ExitOverlayModal({ disabled = false }) {
  const mainSettings = useStoreState((state) => state.siteSettingsModel.mainSettings);
  const assetsBaseUrl = useStoreState(
    (state) => state.siteSettingsModel.webAssetsBaseUrlSettings.CdnImage
  );

  const [show, setShow] = useState(false);
  const hasBeenDisplayedRef = useRef(false);
  const pageLoadedAtRef = useRef(Date.now());
  const inactivityTimerRef = useRef(null);

  const isPrequal = mainSettings?.Flow?.toUpperCase() === "PREQUAL";
  const assetsUrl = assetsBaseUrl?.replace("applicationweb/", "") ?? "";
  const sessionKey = `exit-overlay-${window.location.hostname}-${mainSettings?.Flow ?? ""}`;

  useEffect(() => {
    if (disabled) {
      return;
    }

    hasBeenDisplayedRef.current = sessionStorage.getItem(sessionKey) === "true";

    if (hasBeenDisplayedRef.current) {
      return;
    }

    const isDesktop = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

    const showExitOverlay = () => {
      if (document.hidden || disabled || hasBeenDisplayedRef.current) {
        return;
      }

      hasBeenDisplayedRef.current = true;
      sessionStorage.setItem(sessionKey, "true");
      clearTimeout(inactivityTimerRef.current);
      setShow(true);
    };

    if (isDesktop) {
      const handleExitIntent = (event) => {
        const isEligible = Date.now() - pageLoadedAtRef.current >= eligibilityPeriodMs;
        const isLeavingPage = event.relatedTarget === null && event.clientY <= 10;

        if (isEligible && isLeavingPage) {
          showExitOverlay();
        }
      };

      document.addEventListener("mouseout", handleExitIntent);

      return () => {
        document.removeEventListener("mouseout", handleExitIntent);
      };
    }

    const restartInactivityTimer = () => {
      clearTimeout(inactivityTimerRef.current);

      if (document.hidden || disabled || hasBeenDisplayedRef.current) {
        return;
      }

      inactivityTimerRef.current = setTimeout(showExitOverlay, eligibilityPeriodMs);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearTimeout(inactivityTimerRef.current);
        return;
      }

      restartInactivityTimer();
    };

    const activityEvents = [
      "pointerdown",
      "touchstart",
      "scroll",
      "keydown",
      "input",
      "change",
      "focusin"
    ];

    activityEvents.forEach((eventName) => {
      document.addEventListener(eventName, restartInactivityTimer, true);
    });

    document.addEventListener("visibilitychange", handleVisibilityChange);
    restartInactivityTimer();

    return () => {
      clearTimeout(inactivityTimerRef.current);

      activityEvents.forEach((eventName) => {
        document.removeEventListener(eventName, restartInactivityTimer, true);
      });

      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [disabled, sessionKey]);

  const closeExitOverlay = () => {
    setShow(false);
  };

  const desktopBackgroundImage = {
    backgroundImage: `url('${assetsUrl}appsites/exit-overlay-desktop.png')`,
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    minHeight: "100%",
    width: "100%",
    borderTopLeftRadius: "0.5rem",
    borderBottomLeftRadius: "0.5rem"
  };

  const mobileBackgroundImage = {
    backgroundImage: `url('${assetsUrl}appsites/exit-overlay-mobile.jpg')`,
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    minHeight: "200px",
    width: "100%",
    borderTopLeftRadius: "0.5rem",
    borderTopRightRadius: "0.5rem"
  };

  return (
    <Modal
      size="lg"
      show={show}
      onHide={closeExitOverlay}
      backdrop="static"
      centered
      dialogClassName="exit-overlay-modal"
    >
      <Modal.Body className="p-0 position-relative">
        <button
          type="button"
          onClick={closeExitOverlay}
          aria-label="Close"
          style={{
            position: "absolute",
            top: "10px",
            right: "15px",
            background: "transparent",
            border: "none",
            color: "#fff",
            fontSize: "1.8rem",
            fontWeight: "bold",
            cursor: "pointer",
            zIndex: 10,
            lineHeight: "1"
          }}
        >
          &times;
        </button>

        <div className="row g-0" style={{ minHeight: "500px" }}>
          <div className="col-md-5 d-none d-md-block">
            <div style={desktopBackgroundImage} />
          </div>

          <div className="col-12 d-block d-md-none p-0">
            <div style={mobileBackgroundImage} />
          </div>

          <div
            className="col-12 col-md-7 d-flex flex-column justify-content-center align-items-start p-4"
            style={{
              backgroundColor: "#0D0E1A",
              color: "#fff",
              position: "relative"
            }}
          >
            <h3 className="fw-bold mt-3 text-white exitTitle">
              ARE YOU SURE YOU WANT TO LEAVE?
            </h3>

            <p className="mb-4 exitSubtitle">
              You may be eligible for a card with a credit limit {isPrequal ? "up to" : "of"} $
              {mainSettings?.CreditLimit ?? 0}!
            </p>

            <Button
              variant="success"
              className="fw-bold px-4 mb-4 exitButton align-self-center"
              onClick={closeExitOverlay}
            >
              See My Offer
            </Button>

            <div className="d-flex align-items-center justify-content-center w-100 mt-2">
              <span className="text-white exitFooter ml-2" style={{ display: "block", textAlign: "center" }}>
                {isPrequal ? "No impact on your credit" : "Join the millions of happy customers"}
              </span>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default ExitOverlayModal;